<template>
  <div>
    <!-- Charts section -->
    <div class="flex flex-col md:flex-row gap-0 mb-6 w-full">
      <div class="grid md:grid-rows-3 max-md:grid-cols-3 gap-3 md:w-[210px] flex-shrink-0">
        <div
          v-for="(chart, index) in charts"
          :key="index"
          class="flex w-full relative max-md:flex-col"
        >
          <div
            class="flex flex-row items-center gap-4 w-full rounded-2xl p-3 md:px-4 md:py-2 cursor-pointer transition-all border border-[#F4F8FF]"
            :class="
              chartTab === index
                ? 'bg-[#F4F8FF] rounded-b-none md:rounded-r-none md:rounded-l-2xl'
                : 'bg-white border-[#D9E5FF]'
            "
            @click="chartTab = index"
          >
            <div class="w-8 h-8 max-md:hidden">
              <component
                :is="chart.icon"
                class="size-8"
                :style="{ fill: chartTab === index ? color : '#3A3D4499' }"
              />
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex flex-row items-center justify-between mb-2 md:hidden">
                <component
                  :is="chart.icon"
                  class="fill-txopacity size-6 flex-shrink-0"
                  :style="chartTab === index ? { fill: color } : {}"
                />
                <Icon
                  name="material-symbols-light:chevron-right"
                  class="size-6 transition-all duration-300 flex-shrink-0 -mr-2"
                  :class="chartTab === index ? 'rotate-90' : ''"
                  :style="{ color }"
                />
              </div>
              <div class="text-txopacity md:text-sm text-[0.6875rem]">{{ chart.title }}</div>
              <div class="text-txprimary font-bold text-base mt-1">{{ chart.data }}</div>
            </div>
            <div class="max-md:hidden">
              <Icon
                name="akar-icons:chevron-left"
                class="text-sm"
                :style="{ color }"
                :class="chartTab === index ? 'rotate-0' : 'rotate-180'"
              />
            </div>
          </div>
          <div
            class="h-2 md:hidden transition-all bg-[#F4F8FF]"
            :class="chartTab === index ? 'opacity-100' : 'opacity-0'"
          />
          <div
            class="min-w-3 w-3 h-full transition-all max-md:hidden rounded-none bg-[#F4F8FF]"
            :class="chartTab === index ? 'opacity-100' : 'opacity-0'"
          />
          <div
            v-if="index > 0"
            class="absolute right-0 -top-6 size-6 transition-all max-md:hidden rotate-90"
            :class="chartTab === index ? 'opacity-100' : 'opacity-0'"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M24 0H0C22.4348 1.5 24 24 24 24V0Z" fill="#F4F8FF" />
            </svg>
          </div>
          <div
            v-if="index < charts.length - 1"
            class="absolute right-0 -bottom-6 size-6 transition-all max-md:hidden"
            :class="chartTab === index ? 'opacity-100' : 'opacity-0'"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M24 0H0C22.4348 1.5 24 24 24 24V0Z" fill="#F4F8FF" />
            </svg>
          </div>
        </div>
      </div>

      <!-- Right: chart -->
      <div
        class="flex-1 rounded-2xl pl-4 pr-2 pt-2 pb-4 min-w-[280px] flex flex-col transition-all bg-[#F4F8FF]"
        :class="{
          'rounded-tl-none': chartTab === 0,
          'rounded-bl-none': chartTab === charts.length - 1,
        }"
      >
        <!-- Period + diffs header -->
        <div class="flex items-center justify-between mb-3 flex-wrap gap-2 pr-4">
          <div class="flex gap-1 rounded-2xl p-1 w-fit bg-[#F4F8FF]">
            <button
              v-for="period in periods"
              :key="period.value"
              class="px-4 py-2 text-xs rounded-xl border border-white transition-colors bg-white"
              :style="{
                borderColor: selectedPeriod === period.value ? color : 'white',
                color: selectedPeriod === period.value ? color : '',
              }"
              @click="selectedPeriod = period.value"
            >
              {{ period.label }}
            </button>
          </div>
          <div class="flex items-center gap-3 text-sm flex-wrap">
            <template v-if="chartTab === 0">
              <span
                :class="diffs.subscribersDiff24h >= 0 ? 'font-bold' : 'text-red-500 font-bold'"
                :style="diffs.subscribersDiff24h >= 0 ? { color } : {}"
              >
                {{ diffs.subscribersDiff24h > 0 ? "+" : "" }}{{ diffs.subscribersDiff24h || 0 }}
              </span>
              <span class="text-[#3a3d44]/50 -ml-2">24ч</span>
              <span
                :class="diffs.subscribersDiff7d >= 0 ? 'font-bold' : 'text-red-500 font-bold'"
                :style="diffs.subscribersDiff7d >= 0 ? { color } : {}"
              >
                {{ diffs.subscribersDiff7d > 0 ? "+" : "" }}{{ diffs.subscribersDiff7d || 0 }}
              </span>
              <span class="text-[#3a3d44]/50 -ml-2">7д</span>
              <span
                :class="diffs.subscribersDiff30d >= 0 ? 'font-bold' : 'text-red-500 font-bold'"
                :style="diffs.subscribersDiff30d >= 0 ? { color } : {}"
              >
                {{ diffs.subscribersDiff30d > 0 ? "+" : "" }}{{ diffs.subscribersDiff30d || 0 }}
              </span>
              <span class="text-[#3a3d44]/50 -ml-2">30д</span>
            </template>
            <template v-if="chartTab === 1">
              <span
                :class="diffs.viewsDiff24h >= 0 ? 'font-bold' : 'text-red-500 font-bold'"
                :style="diffs.viewsDiff24h >= 0 ? { color } : {}"
              >
                {{ diffs.viewsDiff24h > 0 ? "+" : "" }}{{ diffs.viewsDiff24h || 0 }}
              </span>
              <span class="text-[#3a3d44]/50 -ml-2">24ч</span>
              <span
                :class="diffs.viewsDiff7d >= 0 ? 'font-bold' : 'text-red-500 font-bold'"
                :style="diffs.viewsDiff7d >= 0 ? { color } : {}"
              >
                {{ diffs.viewsDiff7d > 0 ? "+" : "" }}{{ diffs.viewsDiff7d || 0 }}
              </span>
              <span class="text-[#3a3d44]/50 -ml-2">7д</span>
              <span
                :class="diffs.viewsDiff30d >= 0 ? 'font-bold' : 'text-red-500 font-bold'"
                :style="diffs.viewsDiff30d >= 0 ? { color } : {}"
              >
                {{ diffs.viewsDiff30d > 0 ? "+" : "" }}{{ diffs.viewsDiff30d || 0 }}
              </span>
              <span class="text-[#3a3d44]/50 -ml-2">30д</span>
            </template>
          </div>
        </div>

        <!-- Line chart -->
        <UINewLineChart
          v-if="showStatistic[1]?.length"
          :dates="showStatistic[0]"
          :series="showStatistic[1]"
          :show-bottom-title="false"
          class="!h-[180px] !w-full !min-w-0"
          :param="charts[chartTab].title"
          :lineColor="color"
        />
        <UINewLineChart
          v-else
          class="!h-[180px] !w-full !min-w-0"
          :is-hide="true"
          :showBottomTitle="false"
          :dates="[
            '01.01 01:00',
            '01.01 02:00',
            '01.01 03:00',
            '01.01 04:00',
            '01.01 05:00',
            '01.01 06:00',
            '01.01 07:00',
            '01.01 08:00',
            '01.01 09:00',
            '01.01 10:00',
          ]"
          :series="[1, 3, 2, 4, 5, 6, 5, 7, 9, 8]"
          :lineColor="color"
        />
      </div>
    </div>

    <!-- Bottom stats: Пол / Возраст / География / Категории -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
      <!-- Пол -->
      <div class="bg-[#f1f4f9] rounded-2xl p-4">
        <div class="text-[#3a3d44] text-sm font-medium mb-3">Пол</div>
        <div class="flex justify-center py-2">
          <UINewDonutChart
            v-if="
              place?.subscribersStatistic?.femalePercent ||
              place?.subscribersStatistic?.malePercent
            "
            :female-percent="place.subscribersStatistic.femalePercent"
            :male-percent="place.subscribersStatistic.malePercent"
            :size="80"
            :stroke-width="8"
          />
          <div v-else class="size-[80px] rounded-full border-[8px] border-[#e8edf5]" />
        </div>
        <div class="space-y-2 mt-2">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1.5">
              <div class="size-2 rounded-full bg-[#F846C3]" />
              <span class="text-[#3a3d44]/60 text-xs">Девушки</span>
            </div>
            <span class="text-xs" :style="{ color }">
              {{ place?.subscribersStatistic?.femalePercent ?? 0 }}%
            </span>
          </div>
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1.5">
              <div class="size-2 rounded-full bg-primary" />
              <span class="text-[#3a3d44]/60 text-xs">Мужчины</span>
            </div>
            <span class="text-xs" :style="{ color }">
              {{ place?.subscribersStatistic?.malePercent ?? 0 }}%
            </span>
          </div>
        </div>
      </div>

      <!-- Возраст -->
      <div class="bg-[#f1f4f9] rounded-2xl p-4 relative">
        <div class="text-[#3a3d44] text-sm font-medium mb-3">Возраст</div>
        <div v-if="agesData" class="space-y-2">
          <UINewPercentBar
            v-for="(item, i) in agesData"
            :key="i"
            :label="item.label"
            :percent="item.percent"
            :color="color"
          />
        </div>
        <div v-else>
          <div class="space-y-3 blur-sm opacity-40">
            <div v-for="w in [16, 12, 8, 10, 6]" :key="w" class="flex items-center justify-between">
              <div class="bg-[#3a3d44]/40 rounded-full h-2.5" :style="{ width: w * 4 + 'px' }" />
              <div class="bg-primary/40 rounded-full h-2.5" :style="{ width: w * 3 + 'px' }" />
            </div>
          </div>
          <div class="absolute inset-0 flex items-center justify-center text-[#3a3d44]/50 text-xs">
            Нет данных
          </div>
        </div>
      </div>

      <!-- География -->
      <div class="bg-[#f1f4f9] rounded-2xl p-4 relative">
        <div class="text-[#3a3d44] text-sm font-medium mb-3">География</div>
        <div class="space-y-3 blur-sm opacity-40">
          <div v-for="w in [20, 16, 12, 8, 6]" :key="w" class="flex items-center justify-between">
            <div class="bg-[#3a3d44]/40 rounded-full h-2.5" :style="{ width: w * 4 + 'px' }" />
            <div class="bg-primary/40 rounded-full h-2.5" :style="{ width: w * 3 + 'px' }" />
          </div>
        </div>
        <div class="absolute inset-0 flex items-center justify-center text-[#3a3d44]/50 text-xs">
          Нет данных
        </div>
      </div>

      <!-- Категории -->
      <div class="bg-[#f1f4f9] rounded-2xl p-4">
        <div class="text-[#3a3d44] text-sm font-medium mb-3">Категории</div>
        <div class="flex flex-wrap gap-2">
          <div
            v-for="(cat, i) in place?.categories"
            :key="i"
            class="bg-white text-[#3a3d44] text-xs rounded-lg px-2.5 py-1.5"
          >
            {{ cat.name }}
          </div>
          <div v-if="!place?.categories?.length" class="text-[#3a3d44]/50 text-xs">Нет данных</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import IUsers from "~/components/UINew/icons/IUsers.vue";
  import IViews from "~/components/UINew/icons/IViews.vue";
  import ILike from "~/components/UINew/icons/ILike.vue";
  import dayjs from "dayjs";
  import { fetchApi, FetchApiOrigin } from "~/common/API/baseApi";

  const props = withDefaults(defineProps<{ place: any; color?: string }>(), { color: "#3176FF" });

  const chartTab = ref(0);
  const selectedPeriod = ref<"month" | "year">("month");
  const periods = [
    { label: "По дням", value: "month" as const },
    { label: "По месяцам", value: "year" as const },
  ];

  const diffs = ref({
    subscribersDiff24h: 0,
    subscribersDiff7d: 0,
    subscribersDiff30d: 0,
    viewsDiff24h: 0,
    viewsDiff7d: 0,
    viewsDiff30d: 0,
  });

  const statData = ref<any>(null);

  const showStatistic = computed(() => {
    const type = chartTab.value === 0 ? "subscribers" : chartTab.value === 1 ? "views24h" : "er";
    const series = statData.value?.[type]?.map((item: any) => item.value) || [];
    const dates = statData.value?.[type]?.map((item: any) => item.name) || [];
    return [dates, series];
  });

  const charts = computed(() => [
    {
      title: "Подписчики",
      icon: IUsers,
      data: (props.place?.subscribersStatistic?.count || 0).toLocaleString(),
    },
    {
      title: "Просмотры",
      icon: IViews,
      data: (props.place?.viewsStatistic?.countLast24h || 0).toLocaleString(),
    },
    {
      title: "ERR",
      icon: ILike,
      data: (props.place?.er || 0) + "%",
    },
  ]);

  const agesData = computed(() => {
    const age = props.place?.subscribersStatistic?.age;
    if (!age?.some((item: any) => item.value > 0)) return null;
    return [
      { label: "Менее 18", percent: age[0]?.value ?? 0 },
      { label: "18-24", percent: age[1]?.value ?? 0 },
      { label: "25-34", percent: age[2]?.value ?? 0 },
      { label: "35-44", percent: age[3]?.value ?? 0 },
      { label: "45+", percent: age[4]?.value ?? 0 },
    ];
  });

  const fetchStats = async () => {
    if (!props.place?.id) return;
    const result: any = await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint: `/place/telegram/daily-stats`,
      params: {
        id: props.place.id,
        period: selectedPeriod.value,
        interval: selectedPeriod.value === "year" ? "month" : "day",
      },
    });

    const formated: any = { subscribers: [], views24h: [], er: [] };
    if (result?.records) {
      result.records.forEach((record: any) => {
        const fmt = selectedPeriod.value === "year" ? "MM.YYYY" : "DD.MM.YYYY";
        const date = dayjs(record.createdAt).format(fmt);
        if (record.subscribers != null)
          formated.subscribers.push({ name: date, value: record.subscribers });
        if (record.views != null) formated.views24h.push({ name: date, value: record.views });
        if (record.er != null) formated.er.push({ name: date, value: record.er });
      });
    }

    diffs.value = result?.diffs ?? diffs.value;
    statData.value = formated;
  };

  watch(selectedPeriod, fetchStats);
  watch(
    () => props.place?.id,
    () => fetchStats()
  );

  onMounted(fetchStats);
</script>
